import React from 'react';
import { FiShoppingCart } from 'react-icons/fi';

const DocumentHeader = ({
  stickyOffsetClassName = 'top-24',
  containerClassName = 'bg-gray-100 px-4',
  title,
  onTitleClick,
  headerActions,
  totalAmount,
  totalAmountClassName = 'text-blue-500',
  showCartButton = true,
  onCartClick,
  showMobileCartAmountUnderIcon = false,
  mobileCartAmountClassName = 'text-[11px] font-semibold text-blue-600 leading-none md:hidden',
  clientName,
  clientRut,
  clientGiro,
  clientType,
  showClientType = true,
  emptyClientLabel = 'Venta Detalle',
  variant = 'sale',
  showEmptyClientType = true,
  clientMessage,
  clientMessageType = 'info',
  canChangeClientType = false,
  onChangeClientType,
  clientTypeOptions = [],
  onSelectClientType,
  allowClientTypeToggleOff = false,
  clientTypeTitle = 'Tipo de venta:',
  emptyClientTypeValueLabel = 'Todos',
  clientActions,
  clientSectionClassName = 'min-w-0 lg:flex-1',
  middleContent,
  middleContentClassName = 'hidden lg:flex lg:flex-1 lg:items-start lg:justify-center',
  rightContent,
  rightContentClassName = 'w-full md:w-[360px] lg:w-[420px]',
}) => {
  const hasClient = Boolean(clientName);
  const hasClientType = Boolean(clientType);
  const canEditType = Boolean(canChangeClientType && onChangeClientType);
  const showTypeTabs =
    Boolean(
      canChangeClientType
      && typeof onSelectClientType === 'function'
      && Array.isArray(clientTypeOptions)
      && clientTypeOptions.length
    );

  const titleRowClassName = `flex justify-between items-center${onTitleClick ? ' cursor-pointer' : ''}`;

  const renderTypeButton = () =>
    canEditType && !showTypeTabs ? (
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onChangeClientType();
        }}
        className="text-xs font-semibold text-blue-600 hover:text-blue-800"
        title="Cambiar tipo"
        aria-label="Cambiar tipo de venta"
      >
        Cambiar
      </button>
    ) : null;

  const renderTypeTabs = () => {
    if (!showTypeTabs) return null;
    return (
      <div className="flex flex-wrap gap-1 pointer-events-auto relative z-20">
        {clientTypeOptions.map((option) => {
          const isActive = clientType === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                if (allowClientTypeToggleOff && isActive) {
                  onSelectClientType('');
                  return;
                }
                onSelectClientType(option.value);
              }}
              className={`px-4 py-2 text-sm font-semibold border transition cursor-pointer rounded-none ${
                isActive
                  ? 'bg-blue-100 text-blue-800 border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:text-gray-800'
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    );
  };

  const renderClientTypeRow = () => {
    if (!showClientType) return null;
    return (
      <div className="mt-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold whitespace-nowrap">{clientTypeTitle}</span>
          {!showTypeTabs && (
            <>
              <span className="text-sm">{clientType || emptyClientTypeValueLabel}</span>
              {renderTypeButton()}
            </>
          )}
          {showTypeTabs && renderTypeTabs()}
        </div>
      </div>
    );
  };

  const renderEmptyClientInfo = () => {
    if (variant === 'quotation') {
      return (
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-md font-bold">{emptyClientLabel}</h2>
            {clientActions}
        {showEmptyClientType && hasClientType && !showTypeTabs && (
          <>
            <span className="text-sm">{clientType}</span>
            {renderTypeButton()}
          </>
        )}
      </div>
      {showEmptyClientType && showTypeTabs && (
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold whitespace-nowrap">{clientTypeTitle}</span>
          {renderTypeTabs()}
        </div>
      )}
      {clientMessage && (
        <p
          className={`text-xs ${
                clientMessageType === 'error' ? 'text-red-600' : 'text-green-700'
              }`}
            >
              {clientMessage}
            </p>
          )}
        </div>
      );
    }

    return (
      <div>
        <div className="flex items-center gap-3">
          <h2 className="text-md font-bold">{emptyClientLabel}</h2>
          {clientActions}
        </div>
        {showEmptyClientType && (
          <div className="mt-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold whitespace-nowrap">{clientTypeTitle}</span>
              {!showTypeTabs && (
                <>
                  <span className="text-sm">{clientType || emptyClientTypeValueLabel}</span>
                  {renderTypeButton()}
                </>
              )}
              {showTypeTabs && renderTypeTabs()}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`sticky ${stickyOffsetClassName} pt-2 z-20 ${containerClassName}`}>
      <div className={titleRowClassName} onClick={onTitleClick}>
        <h2 className="text-md font-bold">{title}</h2>
        <div
          className="flex items-center gap-x-4"
          onClick={(event) => event.stopPropagation()}
        >
          {headerActions}
          {totalAmount != null && (
            <span className={totalAmountClassName}>${Number(totalAmount).toLocaleString('de-DE')}</span>
          )}
          {showCartButton && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onCartClick?.(event);
              }}
              className="text-gray-700 hover:text-black p-2 relative flex flex-col items-center"
            >
              <FiShoppingCart size={24} />
              {showMobileCartAmountUnderIcon && totalAmount != null && (
                <span className={mobileCartAmountClassName}>
                  ${Number(totalAmount).toLocaleString('de-DE')}
                </span>
              )}
            </button>
          )}
        </div>
      </div>

      <div className="mt-2 flex flex-col gap-2 lg:flex-row lg:items-start lg:gap-4">
        <div className={clientSectionClassName}>
          {hasClient ? (
            <>
              <div className="flex items-center gap-3 mb-1">
                <h2 className="text-md font-bold">
                  Cliente: {clientName}, {clientRut}
                </h2>
                {clientActions}
              </div>
              {clientGiro && <p className="text-sm text-gray-700 mb-1">Giro: {clientGiro}</p>}
              {renderClientTypeRow()}
            </>
          ) : (
            renderEmptyClientInfo()
          )}
        </div>
        {middleContent && <div className={middleContentClassName}>{middleContent}</div>}
        {rightContent && <div className={rightContentClassName}>{rightContent}</div>}
      </div>
    </div>
  );
};

export default DocumentHeader;
